import imagePlaceholder from '@/assets/placeholder.png';
import Constants from '@/Constants';
import Item from '@/models/Item';
import List, { AddToListRequest } from '@/models/List';
import { ApplicationState } from '@/store';
import { getItensRequest } from '@/store/item/action';
import { addItemsToListClear, addItemsToListRequest, deleteItemFromListRequest, getListItemsRequest, listItemsClear } from '@/store/listItems/action';
import '@/styles/variables.scss';
import React, { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import _ from 'underscore';
import ConfirmDialog, { PropsType as ConfirmDialogPropsType } from '../ConfirmDialog';
import Filters, { orderByFilterEnum } from '../Filter';
import Loader from '../Loader';
import ItemCard from './ItemCard';
import './style.scss';

interface AddToListProps {
    handleBack: () => void;
    list: List;
}

const AddToList = ({ list: createdList }: AddToListProps) => {
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const { data: identity } = useSelector((state: ApplicationState) => state.identity);
    const { listItems, loading: listItemsLoading, success } = useSelector((state: ApplicationState) => state.listItems);
    const {
        data: { itens },
        loading,
    } = useSelector((state: ApplicationState) => state.itens);

    const [loadedList, setLoadedList] = useState<List>(createdList);
    const [localItems, setLocalItems] = useState<Item[]>([]);
    const [invalidItens, setInvalidItens] = useState(0);
    const [selectedItems, setSelectedItems] = useState<Item[]>([]);
    const [savedList, setSavedList] = useState<Item[]>([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [itensLoading, setItensLoading] = useState(true);
    const [enableSave, setEnableSave] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalConfig, setModalConfig] = useState<ConfirmDialogPropsType>({} as ConfirmDialogPropsType);
    const [redirectToListEdit, setRedirectToListEdit] = useState(false);

    // filters
    const [searchName, setSearchName] = useState('');
    const [slideRange, setSlideRange] = useState<number[]>([0, 90]);
    const [fakeSlideRange, setFakeSlideRange] = useState<number[]>([0, 90]);
    const [orderBy, setOrderBy] = useState(orderByFilterEnum.ALPHABETICAL);
    const [mediaType, setMediaType] = useState({ VIDEO: false, AUDIO: false, HTML: false, EXTERNAL: false });
    const [language, setLanguage] = useState({ pt: false, en: false, '': false });
    const [visibility, setVisibility] = useState({ public: false, private: false });

    const dispatch = useDispatch();
    const history = useHistory();

    useEffect(() => {
        if (createdList.id && !listItems) dispatch(getListItemsRequest(createdList.id));
    }, [dispatch, createdList]);

    useEffect(() => {
        if (listItems && itens) {
            const contentList: Item[] = _.pluck(listItems, 'content');
            if (selectedItems.length === 0) setSelectedItems([...contentList]);
            setSavedList([...contentList]);
        }
    }, [listItems, itens]);

    useEffect(() => {
        setItensLoading(loading);
    }, [loading]);

    useEffect(() => {
        if (success) {
            dispatch(addItemsToListClear());
            if (redirectToListEdit) {
                navigateToEditListScreen();
            } else {
                navigateToPlaylistScreen();
            }
        }
    }, [dispatch, history, success, loadedList, redirectToListEdit]);

    useEffect(() => {
        if (!loading) setInitialLoading(false);
        if (itens) {
            const filteredItens = itens.filter((item) => {
                return !(listItems && listItems.find((i) => i.content.id === item.id));
            });
            setLocalItems(filteredItens);
        }
    }, [loading, itens, listItems]);

    useEffect(() => {
        const {
            user: { orgId, applicationId },
        } = identity;

        let visibilityParam = undefined;

        if (visibility.private && !visibility.public) visibilityParam = '0';
        if (!visibility.private && visibility.public) visibilityParam = '1';

        let media: string | undefined = '';

        if (mediaType.AUDIO) media = 'AUDIO';
        if (mediaType.EXTERNAL) media += media.length > 0 ? ',EXTERNAL' : 'EXTERNAL';
        if (mediaType.HTML) media += media.length > 0 ? ',HTML' : 'HTML';
        if (mediaType.VIDEO) media += media.length > 0 ? ',VIDEO' : 'VIDEO';

        if (media.length <= 0) media = undefined;

        let locale: string | undefined = '';

        if (!language.pt && !language.en) locale = undefined;
        else if (language.pt && language.en) locale = undefined;
        else locale = language.pt ? 'pt' : 'en';

        dispatch(
            getItensRequest({
                orgId,
                applicationId,
                name: searchName,
                minWorkload: slideRange[0].toString(),
                maxWorkload: slideRange[1] < 90 ? slideRange[1].toString() : undefined,
                status: Constants.lessonStatus.PUBLISHED,
                visibility: visibilityParam,
                orderBy: orderBy === orderByFilterEnum.ALPHABETICAL ? undefined : orderBy,
                mediaType: media,
                locale,
                moduleId: Constants.PLAYLIST_MODULE_ID,
            }),
        );
    }, [dispatch, identity, searchName, slideRange, visibility.private, visibility.public, orderBy, mediaType, language]);

    useEffect(() => {
        setInvalidItens(0);
        if (createdList && createdList.locale) {
            selectedItems.map((item) => (item.locale !== createdList.locale ? setInvalidItens((prevState) => prevState + 1) : null));
        }
    }, [selectedItems, setInvalidItens, createdList]);

    useEffect(() => {
        setEnableSave(selectedItems.length > 0 && selectedItems.length > savedList.length);
        setLocalItems(localItems.filter((itm) => selectedItems.findIndex((i) => i.id === itm.id) < 0));
    }, [selectedItems]);

    useEffect(() => {
        return () => {
            dispatch(listItemsClear());
        };
    }, []);

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchName(e.target.value);
    };

    const handleAddToList = (item: Item) => {
        setSelectedItems((prevState) => [...prevState, item]);
    };

    const handleRemoveFromList = (item: Item) => {
        setSelectedItems((prevState) => prevState.filter((itm) => item.id !== itm.id));
        setLocalItems((prevState) => [...prevState, item]);

        if (listItems && listItems.length > 0) {
            const found = listItems.find((i) => item.id === i.content.id);
            if (found) {
                setSavedList((prevState) => prevState.filter((itm) => item.id !== itm.id));
                dispatch(deleteItemFromListRequest({ lessonId: found.id, listId: createdList.id }));
            }
        }
    };

    const handleAddToListRequest = (willPublish = false) => {
        const payload: AddToListRequest[] = [];
        selectedItems.map((listItem) => {
            if (listItems && listItems.length > 0) {
                const found = listItems.find((i) => listItem.id === i.content.id);
                if (!found)
                    payload.push({
                        mediaLesson: {
                            '@class': '.LessonContentTO',
                            id: listItem.id,
                            course: {
                                id: Number(createdList.id),
                            },
                        },
                    });
                return listItem;
            }
            payload.push({
                mediaLesson: {
                    '@class': '.LessonContentTO',
                    id: listItem.id,
                    course: {
                        id: Number(createdList.id),
                    },
                },
            });
            return listItem;
        });
        dispatch(addItemsToListRequest({ payload, willPublish, history, listId: Number(createdList.id) }));
    };

    const handleBack = () => {
        history.go(-1);
    };

    const navigateToPlaylistScreen = () => {
        history.push('/contents/playlist/lists');
    };

    const navigateToEditListScreen = () => {
        history.push(`/lists/${createdList ? createdList.id : '0'}/edit`);
    };

    const showRemoveButtonItemCard = (item: Item) => {
        const isSavedItem = savedList.some((savedItem: Item) => savedItem.id === item.id);

        if (!isSavedItem) {
            return true;
        }

        const isPublishedList = createdList && createdList.status === Constants.lessonStatus.PUBLISHED.toString();
        return !isPublishedList;
    };

    const onBackClickHandler = (e: SyntheticEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setModalConfig({
            leftButtonTitle: strings.CONFIRM_EXIT,
            leftClick: navigateToPlaylistScreen,
            rightButtonTitle: strings.NO,
            rightClick: () => setShowModal(false),
            icon: Constants.icons.ANNOUNCEMENT,
            content: strings.LEAVE_SECTION_MESSAGE,
        } as ConfirmDialogPropsType);

        setShowModal(true);
    };

    const onSaveAndRedirectToListEditHandler = () => {
        setRedirectToListEdit(true);
        handleAddToListRequest();
    };

    const onEditListClickHandler = (e: SyntheticEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (_.isEqual(savedList, selectedItems)) {
            navigateToEditListScreen();
        } else {
            setModalConfig({
                leftButtonTitle: strings.YES,
                leftClick: onSaveAndRedirectToListEditHandler,
                rightButtonTitle: strings.NO,
                rightClick: navigateToEditListScreen,
                icon: Constants.icons.ANNOUNCEMENT,
                content: strings.SAVE_ITEMS_LIST_CONFIRMATION,
            } as ConfirmDialogPropsType);

            setShowModal(true);
        }
    };

    return (
        <>
            {initialLoading && <Loader />}
            {!initialLoading && (
                <>
                    <ConfirmDialog
                        content={modalConfig.content}
                        icon={modalConfig.icon}
                        leftButtonTitle={modalConfig.leftButtonTitle}
                        leftClick={() => modalConfig.leftClick && modalConfig.leftClick()}
                        rightButtonTitle={modalConfig.rightButtonTitle}
                        rightClick={() => modalConfig.rightClick && modalConfig.rightClick()}
                        show={showModal}
                    />

                    <div className="list">
                        <div className="back-button go-back navbar navbar-expand sub-nav">
                            <div>
                                <a href="#" onClick={(e: SyntheticEvent) => onBackClickHandler(e)}>
                                    <i className="material-icons-outlined align-middle">{Constants.icons.ARROW_LEFT}</i>
                                    <span>{strings.GO_BACK}</span>
                                </a>
                            </div>
                        </div>

                        <div className="title-container">
                            <div className="title">
                                <h1>{strings.LIST_EDIT}</h1>
                            </div>
                        </div>

                        <div className="content">
                            <a className="edit-button" href="#" onClick={(e) => onEditListClickHandler(e)}>
                                <i className="material-icons align-middle" tabIndex={0}>
                                    {Constants.icons.EDIT}
                                </i>
                                <span tabIndex={0}>{strings.EDIT}</span>
                            </a>

                            <div className="title-container">
                                <img alt="" src={(createdList && createdList.mainImage) || imagePlaceholder} />
                                <div>
                                    <span className="title">{loadedList && loadedList.name}</span>
                                    <span className="sub-title-1">{strings.AUTHOR}: </span>
                                    <span className="sub-title-2">{loadedList && loadedList.author}</span>
                                </div>
                            </div>

                            <div className="row list-itens">
                                <div className="list-title">
                                    <h1>{strings.LIST_ITEMS}</h1>
                                    {invalidItens > 0 && (
                                        <div className="error">
                                            <i className="material-icons align-middle">error</i>
                                            <span>
                                                {strings.WE_FOUND}{' '}
                                                <span>
                                                    {invalidItens} {invalidItens > 1 ? strings.INCOMPATIBLE_ITEMS : strings.INCOMPATIBLE_ITEM}
                                                </span>{' '}
                                                {strings.WITH_SELECTED_LANGUAGE}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                {listItemsLoading && <Loader />}
                                {!listItemsLoading && selectedItems.length <= 0 && (
                                    <div className="empty-list">
                                        <i className="material-icons-outlined align-middle">description</i>
                                        <span className="text-one">{strings.LIST_NO_ITEMS}</span>
                                        <span className="text-two">{strings.CHOOSE_ITEMS_BELOW}</span>
                                    </div>
                                )}

                                {!listItemsLoading && selectedItems.length > 0 && (
                                    <div className="itens">
                                        {selectedItems.map((item: Item) => (
                                            <ItemCard
                                                error={createdList ? item.locale !== createdList.locale : false}
                                                handleAddToList={handleAddToList}
                                                handleRemoveFromList={handleRemoveFromList}
                                                item={item}
                                                key={item.id}
                                                showButton={showRemoveButtonItemCard(item)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="submit-buttons">
                                <button className="btn btn-link" onClick={(e: SyntheticEvent) => onBackClickHandler(e)} type="button">
                                    {strings.CANCEL}
                                </button>
                                <button
                                    className={`btn btn-primary btn-sm ${createdList.status === Constants.lessonStatus.DRAFT && 'save-button'}`}
                                    disabled={!enableSave}
                                    onClick={() => handleAddToListRequest()}
                                    type="button"
                                >
                                    {createdList.status === Constants.lessonStatus.DRAFT ? strings.SAVE_AND_CLOSE : strings.SAVE}
                                </button>
                                {createdList.status === Constants.lessonStatus.DRAFT && (
                                    <button
                                        className="btn btn-primary btn-sm"
                                        disabled={selectedItems.length <= 0}
                                        onClick={() => handleAddToListRequest(true)}
                                        type="button"
                                    >
                                        {strings.PUBLISH}
                                    </button>
                                )}
                            </div>

                            <div className="add-to-list">
                                <h1>{strings.LOCATE_AND_SELECT_ITEMS}</h1>
                                <div className="inner-addon left-addon">
                                    <i className="material-icons-outlined align-middle input-icon">search</i>
                                    <input
                                        className="form-control"
                                        onChange={handleSearch}
                                        placeholder="O que você gostaria de buscar?"
                                        type="search"
                                    />
                                </div>

                                <Filters
                                    entityType={Constants.entityTypes.CONTENT}
                                    fakeSlideRange={fakeSlideRange}
                                    language={language}
                                    mediaType={mediaType}
                                    orderBy={orderBy}
                                    setFakeSlideRange={setFakeSlideRange}
                                    setLanguage={setLanguage}
                                    setMediaType={setMediaType}
                                    setOrderBy={setOrderBy}
                                    setSlideRange={setSlideRange}
                                    setVisibility={setVisibility}
                                    slideRange={slideRange}
                                    status={Constants.lessonStatus.ARCHIVED} // to match conditions and render the right filters
                                    visibility={visibility}
                                />
                            </div>

                            {itensLoading ? (
                                <Loader />
                            ) : (
                                <>
                                    <div className="itens">
                                        {localItems &&
                                            localItems.map((item: Item) => (
                                                <ItemCard
                                                    add
                                                    handleAddToList={handleAddToList}
                                                    handleRemoveFromList={handleRemoveFromList}
                                                    item={item}
                                                    key={item.id}
                                                    showButton
                                                />
                                            ))}
                                    </div>
                                    {!itensLoading && localItems.length <= 0 && (
                                        <div className="row list-itens" style={{ marginTop: 0, marginBottom: 60 }}>
                                            <div className="list-title">
                                                <div className="empty-list">
                                                    <i
                                                        className="material-icons-outlined align-middle"
                                                        onClick={handleBack}
                                                        onKeyDown={() => {}}
                                                        role="button"
                                                        tabIndex={0}
                                                    >
                                                        description
                                                    </i>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default AddToList;
